import { OutputData } from "@editorjs/editorjs";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import EditTopic from "./EditTopic";
import TopicsPreview from "./TopicsPreview";
import {
  genTopicUpdateArgs,
  valTopicImages,
  valTopicRequiredItem,
} from "./EditTopicsFunc";
import RequestCompleteNotice from "../../../../common/settings-modal/RequestCompleteNotice";
import NotificationModal from "../../../../common/settings-modal/NotificationModal";
import LoadingSpinner from "../../../../common/LoadingSpinner";
import { SonicTermData } from "../../../../common/classes/SonicTerm";

import SelenaA from "../../../../assets/img/service/selena-a.webp";

import { useAppDispatch, useAppSelector } from "../../../../redux/hooks";
import { getAuthToken } from "../../../../redux/reducers/auth";
import {
  getName,
  getLinkedPages,
  updateLinkedPages,
} from "../../../../redux/reducers/user";

import {
  getCurrentImage,
  getTermOptions,
} from "../../../../utils/GeneralUtils";
import { PageVisibility } from "../../../../utils/PageVisibility";
import { FieldState } from "../../../../utils/ValidationFunctions";
import {
  genNotificationColor,
  genNotificationIcon,
  genNotificationMessage,
  RequestState,
} from "../../../../utils/RequestState";
import { TermType, useGetTerm } from "../../../../utils/useTerm";
import { useUpdateTopics } from "../../../../utils/useTopics";
// import { useGetFacility } from "../../../../utils/useFacility";

const CreateTopicContainer: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const auth_token = useAppSelector(getAuthToken);
  const author_name = useAppSelector(getName);
  const linked_pages = useAppSelector(getLinkedPages);

  const topicTypes = useGetTerm(TermType.TopicTypes, undefined, -1).termData
    .entries;
  const { mutateAsync } = useUpdateTopics([
    "topics",
    {
      auth_token: auth_token,
      per: -1,
      id: linked_pages?.topics,
    },
  ]);

  // const {
  //   items: { facilities },
  // } = useGetFacility({ per: -1 });

  // const genFacilityOptions = () => {
  //   const options: [string, string][] = [["", "施設を選択"]];
  //   facilities.map((f) => {
  //     options.push([f.id, f.name]);
  //   });

  //   return options;
  // };

  const [fieldState, setFieldState] = useState<FieldState>({});
  const [mainImage, setMainImage] = useState<File>();
  const [article, setArticle] = useState<OutputData>();
  const [error, setError] = useState<{ required: boolean; imgs: boolean }>({
    required: false,
    imgs: false,
  });
  const [isChange, setIsChange] = useState<boolean>(false);
  const [isComplete, setIsComplete] = useState<boolean>(false);
  const [isPreview, setIsPreview] = useState<boolean>(false);
  const [requestState, setRequestState] = useState<RequestState>(
    RequestState.Waiting
  );

  const completeHandler = () => {
    navigate("/mypage/topic-settings", { replace: true });
  };

  const handleInputChange = (
    value: File | string | undefined,
    name: string
  ) => {
    const f = fieldState;
    if (value === undefined) {
      if (name !== "sonic_imgs") f[name] = ["", false];
    } else {
      setIsChange(true);
      setRequestState(RequestState.Pending);
      setError({ required: false, imgs: false });
      if (value instanceof File) setMainImage(value);
      else f[name] = [value, true];
    }
    setFieldState(f);
  };

  // set editorjs data
  const handleSaveArticle = (outputData: OutputData) => {
    setArticle(outputData.blocks.length ? outputData : undefined);
    setError({ required: false, imgs: false });
  };

  const handleChangeArticle = () => {
    setIsChange(true);
    setRequestState(RequestState.Pending);
  };

  const handlePreview = () => {
    const valRequired: boolean = valTopicRequiredItem(
      "create",
      fieldState,
      article,
      mainImage
    );
    const valImages: boolean = valTopicImages(mainImage, article);

    if (valRequired === false || valImages === false) {
      setError({ required: !valRequired, imgs: !valImages });
      return;
    }

    setError({ required: false, imgs: false });
    setIsPreview(true);

    if (requestState === RequestState.Success)
      setRequestState(RequestState.Waiting);
  };

  // public application
  const handleSave = async (isPrivate: boolean = false) => {
    if (!auth_token) return;

    const valRequired: boolean = valTopicRequiredItem(
      "create",
      fieldState,
      article,
      mainImage
    );
    const valImages: boolean = valTopicImages(mainImage, article);

    if (valRequired === false || valImages === false) {
      setError({ required: !valRequired, imgs: !valImages });
      return;
    }

    setError({ required: false, imgs: false });

    const updateArgs = genTopicUpdateArgs(
      fieldState,
      article,
      mainImage,
      games
    );

    if (updateArgs === undefined) return;
    if (isPrivate) {
      updateArgs["sonic_status"] = PageVisibility.NotPubliclyVisible;
    }

    setRequestState(RequestState.Doing);

    const res = await mutateAsync({
      auth_token: auth_token,
      update: updateArgs,
    });
    if (res) dispatch(updateLinkedPages({ type: "topics", id: res.id }));

    if (!isPrivate) setIsComplete(true);
    else navigate("/mypage/topic-settings");
  };

  // term_id to term_name
  const getTermName = (termId: string) => {
    for (const category of topicTypes) {
      if (category.id == termId) return category.name;
    }
    return "";
  };

  // MyGame
  const [games, setGames] = useState<Array<SonicTermData>>([]);

  const handleGames = (value: Array<SonicTermData>) => {
    setGames([...value]);
    setRequestState(RequestState.Pending);
  };

  // Scroll up when switching to preview
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [isPreview]);

  useEffect(() => {
    if (requestState === RequestState.Doing) {
      window.scrollTo(0, 0);
    }
  }, [requestState]);

  if (isComplete) {
    return (
      <RequestCompleteNotice
        handler={completeHandler}
        title="公開申請完了"
        text={
          "公開申請が完了しました。\nこれより審査を開始します。\n結果通知までしばらくお待ちください。"
        }
        sub_text="※内容によっては数日お時間をいただく可能性がございます。"
      />
    );
  } else {
    return (
      <>
        {requestState !== RequestState.Doing ? (
          <>
            {isPreview ? (
              <TopicsPreview
                title={
                  fieldState["sonic_name"]?.[1] === true
                    ? fieldState["sonic_name"][0].toString()
                    : undefined
                }
                mainImg={mainImage ? getCurrentImage(mainImage) : undefined}
                type={
                  fieldState["sonic_topic_type"]?.[1] === true
                    ? getTermName(fieldState["sonic_topic_type"][0].toString())
                    : undefined
                }
                startDate={
                  fieldState["sonic_start_date"]?.[1] === true
                    ? Number(fieldState["sonic_start_date"][0])
                    : undefined
                }
                game={games}
                tags={
                  fieldState["sonic_tags"]
                    ? String(fieldState["sonic_tags"][0]).split(" ")
                    : undefined
                }
                article={article}
                authorName={author_name}
                handler={() => setIsPreview(false)}
                handlePrivate={isChange ? () => handleSave(true) : undefined}
                handleSubmit={isChange ? () => handleSave(false) : undefined}
              />
            ) : (
              <EditTopic
                title={
                  fieldState["sonic_name"]?.[1] === true
                    ? fieldState["sonic_name"][0].toString()
                    : undefined
                }
                topicType={
                  fieldState["sonic_topic_type"]?.[1] === true
                    ? fieldState["sonic_topic_type"][0].toString()
                    : undefined
                }
                games={games}
                handleGames={handleGames}
                startDate={
                  fieldState["sonic_start_date"]?.[1] === true
                    ? Number(fieldState["sonic_start_date"][0])
                    : undefined
                }
                tags={
                  fieldState["sonic_tags"]
                    ? String(fieldState["sonic_tags"][0])
                    : undefined
                }
                mainImage={mainImage}
                article={article}
                topicTypes={getTermOptions(topicTypes)}
                // facilityOptions={genFacilityOptions()}
                // facilityId={
                //   fieldState["sonic_facility"]?.[1] === true
                //     ? fieldState["sonic_facility"][0].toString()
                //     : undefined
                // }
                isEdit={false}
                error={error}
                handleInputChange={handleInputChange}
                handlePrivate={isChange ? () => handleSave(true) : undefined}
                handleSubmit={isChange ? () => handleSave(false) : undefined}
                handlePreview={handlePreview}
                deleteMainImage={() => setMainImage(undefined)}
                handleSaveArticle={handleSaveArticle}
                handleChangeArticle={handleChangeArticle}
              />
            )}
          </>
        ) : (
          <LoadingSpinner space={1} />
        )}
        {isChange || requestState !== RequestState.Waiting ? (
          <NotificationModal
            color={genNotificationColor(requestState)}
            icon_class={genNotificationIcon(requestState)}
            message={genNotificationMessage(requestState)}
            img_src={
              requestState === RequestState.Success ? SelenaA : undefined
            }
          />
        ) : null}
      </>
    );
  }
};

export default CreateTopicContainer;
